export type CookieConsent = 'necessary' | 'all'

export const COOKIE_CONSENT_STORAGE_KEY = 'cookieConsent'
export const COOKIE_CONSENT_VERSION = 2

type StoredCookieConsent = {
  version: number
  analytics: boolean
  updatedAt: string
}

const isStoredCookieConsent = (value: unknown): value is StoredCookieConsent =>
  !!value
  && typeof value === 'object'
  && 'version' in value
  && value.version === COOKIE_CONSENT_VERSION
  && 'analytics' in value
  && typeof value.analytics === 'boolean'
  && 'updatedAt' in value
  && typeof value.updatedAt === 'string'

const googleAnalyticsCookiePattern = /^(_ga($|_)|_gid$|_gat($|_)|_gac_)/

const getCookieDeletionDomains = () => {
  const hostname = window.location.hostname
  if (!hostname || hostname === 'localhost' || /^[\d.]+$/.test(hostname)) return ['']

  const domains = new Set<string>(['', hostname, `.${hostname}`])
  const hostnameParts = hostname.split('.').filter(Boolean)

  if (hostnameParts.length > 2) {
    const registrableDomain = hostnameParts.slice(-2).join('.')
    domains.add(registrableDomain)
    domains.add(`.${registrableDomain}`)
  }

  return [...domains]
}

const expireCookie = (name: string, domain: string) => {
  const domainAttribute = domain ? `; domain=${domain}` : ''
  document.cookie = `${name}=; Max-Age=0; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${domainAttribute}; SameSite=Lax`
}

const clearGoogleAnalyticsCookies = () => {
  const cookieNames = document.cookie
    .split(';')
    .map(cookie => cookie.split('=')[0]?.trim())
    .filter((name): name is string => !!name && googleAnalyticsCookiePattern.test(name))

  if (!cookieNames.length) return

  const domains = getCookieDeletionDomains()

  cookieNames.forEach(name => {
    domains.forEach(domain => expireCookie(name, domain))
  })
}

export const getStoredCookieConsent = (): CookieConsent | null => {
  if (typeof window === 'undefined') return null

  const storedConsent = localStorage.getItem(COOKIE_CONSENT_STORAGE_KEY)
  if (storedConsent) {
    try {
      const parsed: unknown = JSON.parse(storedConsent)
      if (isStoredCookieConsent(parsed)) return parsed.analytics ? 'all' : 'necessary'
    } catch {
    }
  }

  localStorage.removeItem(COOKIE_CONSENT_STORAGE_KEY)
  localStorage.removeItem('acceptedCookies')
  clearGoogleAnalyticsCookies()

  return null
}

export const persistCookieConsent = (consent: CookieConsent) => {
  if (typeof window === 'undefined') return

  localStorage.setItem(COOKIE_CONSENT_STORAGE_KEY, JSON.stringify({
    version: COOKIE_CONSENT_VERSION,
    analytics: consent === 'all',
    updatedAt: new Date().toISOString(),
  }))

  if (consent === 'all') {
    localStorage.setItem('acceptedCookies', JSON.stringify({
      date: new Date().toISOString(),
    }))
    return
  }

  localStorage.removeItem('acceptedCookies')
  clearGoogleAnalyticsCookies()
}
